/**
 * Modal simples para gerenciar tags do lead — associa/cria tags reutilizáveis
 * do tenant (armazenadas em pipeline_tags, referenciadas por id em dados_card.tags)
 */

import { useEffect, useMemo, useState, type SyntheticEvent } from 'react';
import { Modal, Select, Input, Tag as AntTag, message } from 'antd';
import { Hash, Pencil, Trash2, Check, X as XIcon } from 'lucide-react';
import { usePipelineTags } from '../hooks/usePipelineTags';
import type { PipelineTag } from '../types';

interface PipelineLeadTagsModalProps {
  open: boolean;
  onClose: () => void;
  /** Array de ids (uuid) de pipeline_tags */
  tags: string[];
  onSave: (tagIds: string[]) => Promise<void>;
  saving?: boolean;
}

const CREATE_PREFIX = '__create__:';

/** Impede que o clique num ícone dentro de uma option do Select dispare a seleção */
function stopOptionClick(e: SyntheticEvent) {
  e.preventDefault();
  e.stopPropagation();
}

export function PipelineLeadTagsModal({
  open,
  onClose,
  tags: initialTagIds,
  onSave,
  saving = false,
}: PipelineLeadTagsModalProps) {
  const { tags: tenantTags, tagsById, createTag, updateTag, deleteTag } = usePipelineTags();
  const [selectedIds, setSelectedIds] = useState<string[]>(initialTagIds);
  const [searchValue, setSearchValue] = useState('');
  const [creating, setCreating] = useState(false);
  const [renamingTag, setRenamingTag] = useState<PipelineTag | null>(null);
  const [renameValue, setRenameValue] = useState('');

  useEffect(() => {
    if (open) {
      setSelectedIds(initialTagIds);
      setSearchValue('');
      setRenamingTag(null);
    }
  }, [open, initialTagIds]);

  const trimmedSearch = searchValue.trim();
  const exactMatch = tenantTags.some(
    (t) => t.nome.toLowerCase() === trimmedSearch.toLowerCase()
  );

  const options = useMemo(() => {
    const base = tenantTags.map((t) => ({ value: t.id, label: t.nome }));
    if (trimmedSearch && !exactMatch) {
      base.push({ value: `${CREATE_PREFIX}${trimmedSearch}`, label: `Criar tag "${trimmedSearch}"` });
    }
    return base;
  }, [tenantTags, trimmedSearch, exactMatch]);

  const handleChange = async (values: string[]) => {
    const createToken = values.find((v) => v.startsWith(CREATE_PREFIX));
    if (createToken) {
      const nome = createToken.slice(CREATE_PREFIX.length);
      setCreating(true);
      try {
        const newTag = await createTag.mutateAsync({ nome });
        setSelectedIds([...values.filter((v) => v !== createToken), newTag.id]);
        setSearchValue('');
      } catch {
        message.error('Não foi possível criar a tag');
      } finally {
        setCreating(false);
      }
      return;
    }
    setSelectedIds(values);
  };

  const startRename = (tag: PipelineTag) => {
    setRenamingTag(tag);
    setRenameValue(tag.nome);
  };

  const cancelRename = () => {
    setRenamingTag(null);
    setRenameValue('');
  };

  const confirmRename = async () => {
    if (!renamingTag) return;
    const nomeTrim = renameValue.trim();
    if (!nomeTrim || nomeTrim === renamingTag.nome) {
      cancelRename();
      return;
    }
    try {
      await updateTag.mutateAsync({ id: renamingTag.id, nome: nomeTrim });
      message.success('Tag renomeada');
      cancelRename();
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Não foi possível renomear a tag');
    }
  };

  const handleDeleteTag = (tag: PipelineTag) => {
    Modal.confirm({
      title: `Excluir tag "${tag.nome}"?`,
      content: 'Ela será removida de todos os leads que a usam. Essa ação não pode ser desfeita.',
      okText: 'Excluir',
      okButtonProps: { danger: true },
      cancelText: 'Cancelar',
      zIndex: 2100,
      onOk: async () => {
        try {
          await deleteTag.mutateAsync(tag.id);
          setSelectedIds((prev) => prev.filter((id) => id !== tag.id));
          message.success('Tag excluída');
        } catch (err) {
          message.error(err instanceof Error ? err.message : 'Não foi possível excluir a tag');
        }
      },
    });
  };

  const handleSave = async () => {
    try {
      await onSave(selectedIds);
      message.success('Tags atualizadas');
      onClose();
    } catch {
      message.error('Não foi possível salvar as tags');
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      className="pipeline-submodal"
      title={
        <span className="flex items-center gap-2 text-sm font-semibold">
          <Hash className="w-4 h-4 text-[#8f9aab]" />
          Gerenciar tags
        </span>
      }
      okText="Salvar"
      cancelText="Cancelar"
      onOk={handleSave}
      confirmLoading={saving}
      destroyOnHidden
      width={400}
      zIndex={2000}
    >
      <div className="space-y-3 pt-1">
        <Select
          mode="multiple"
          placeholder="Buscar ou criar tag"
          value={selectedIds}
          onChange={handleChange}
          onSearch={setSearchValue}
          searchValue={searchValue}
          options={options}
          loading={creating}
          // Substituído pelo indicador à esquerda no optionRender — desativa
          // o ícone de "selecionado" padrão do antd à direita (evitava o
          // aparece/some que deslocava os botões de editar/excluir).
          menuItemSelectedIcon={null}
          filterOption={(input, option) =>
            ((option?.label as string) ?? '').toLowerCase().includes(input.toLowerCase())
          }
          optionRender={(option) => {
            const tag = tagsById.get(option.value as string);
            if (!tag) {
              return <span>{option.label}</span>;
            }
            const isSelected = selectedIds.includes(tag.id);
            return (
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-1.5 min-w-0 truncate">
                  <Check
                    className={`w-3 h-3 flex-shrink-0 text-[#FF3737] ${isSelected ? '' : 'opacity-0'}`}
                  />
                  <span className="truncate">{tag.nome}</span>
                </span>
                <span className="flex items-center gap-0.5 flex-shrink-0">
                  <button
                    type="button"
                    onMouseDown={stopOptionClick}
                    onClick={(e) => {
                      stopOptionClick(e);
                      startRename(tag);
                    }}
                    className="p-1 rounded hover:bg-black/5 text-[#8f9aab] hover:text-[#FF3737]"
                    aria-label={`Renomear tag ${tag.nome}`}
                  >
                    <Pencil className="w-3 h-3" />
                  </button>
                  <button
                    type="button"
                    onMouseDown={stopOptionClick}
                    onClick={(e) => {
                      stopOptionClick(e);
                      handleDeleteTag(tag);
                    }}
                    className="p-1 rounded hover:bg-black/5 text-[#8f9aab] hover:text-red-500"
                    aria-label={`Excluir tag ${tag.nome}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </span>
              </div>
            );
          }}
          tagRender={({ label, value, closable, onClose }) => {
            const tag = tagsById.get(value as string);
            return (
              <AntTag
                closable={closable}
                onClose={onClose}
                className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[12px] border-[#dce3ef]"
                style={{
                  background: tag?.cor ? `${tag.cor}22` : '#f5f6f8',
                  color: tag?.cor || undefined,
                }}
              >
                #{label}
              </AntTag>
            );
          }}
          style={{ width: '100%' }}
        />

        {renamingTag && (
          <div className="flex items-center gap-1.5 p-1.5 rounded-md bg-[#f5f6f8] border border-[#dce3ef]">
            <Input
              size="small"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onPressEnter={confirmRename}
              autoFocus
              placeholder="Novo nome da tag"
            />
            <button
              type="button"
              onClick={confirmRename}
              className="p-1 rounded hover:bg-black/5 text-green-600 flex-shrink-0"
              aria-label="Salvar novo nome"
            >
              <Check className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={cancelRename}
              className="p-1 rounded hover:bg-black/5 text-[#8f9aab] flex-shrink-0"
              aria-label="Cancelar renomeação"
            >
              <XIcon className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {selectedIds.length === 0 && (
          <p className="text-[13px] text-[#8f9aab]">Nenhuma tag neste lead.</p>
        )}
      </div>
    </Modal>
  );
}

export default PipelineLeadTagsModal;
